import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { LogOut, Clock, CheckCircle2, Play, Pause, RefreshCw, Truck, Briefcase, ClipboardList, Timer, TrendingUp, History, Navigation, DollarSign, User, Flame, Trophy, Bell, MapPin, FileText, Settings, Lock, Calendar } from 'lucide-react';
import { TaskList, WeeklyScheduleView, AvailableSlotsView, LocationView, AttendanceHistoryView, EarningsView, KYCProfileView, FleetDistanceView, FleetBadgesView, NotificationBell, SalarySlipsView, HolidayCalendarView } from './EmployeePortalComponents';
import { DutySummaryView } from './DutySummaryView';
import { EmployeeFilesView } from './EmployeeFilesView';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function EmployeePortalDashboard() {
  var navigate = useNavigate();
  var [employee, setEmployee] = useState(null);
  var [dashboard, setDashboard] = useState(null);
  var [tasks, setTasks] = useState([]);
  var [loading, setLoading] = useState(true);
  var [checkingIn, setCheckingIn] = useState(false);
  var [currentLocation, setCurrentLocation] = useState(null);
  var [liveHours, setLiveHours] = useState(null);
  var [activeTab, setActiveTab] = useState('overview');
  var [weeklySchedule, setWeeklySchedule] = useState(null);
  var [weekStart, setWeekStart] = useState(null);
  var [availableSlots, setAvailableSlots] = useState([]);
  var [showChangePassword, setShowChangePassword] = useState(false);
  var [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  var [changingPassword, setChangingPassword] = useState(false);

  useEffect(function() {
    var token = localStorage.getItem('employee_token');
    var employeeData = localStorage.getItem('employee_data');
    if (!token || !employeeData) { navigate('/employee-portal'); return; }
    try { setEmployee(JSON.parse(employeeData)); } 
    catch (e) { navigate('/employee-portal'); }
  }, [navigate]);

  useEffect(function() {
    if (employee && employee.id) { loadData(); }
  }, [employee]);

  useEffect(function() {
    if (!dashboard) return;
    if (!dashboard.is_checked_in) return;
    if (!dashboard.current_shift) return;
    if (!dashboard.current_shift.check_in) return;
    var interval = setInterval(function() {
      var checkIn = new Date(dashboard.current_shift.check_in);
      var hoursWorked = (new Date() - checkIn) / 3600000;
      var dutyHours = dashboard.current_shift.duty_hours || 9;
      setLiveHours({
        worked: hoursWorked.toFixed(2),
        remaining: Math.max(0, dutyHours - hoursWorked).toFixed(2),
        progress: Math.min((hoursWorked / dutyHours) * 100, 100).toFixed(1)
      });
    }, 1000);
    return function() { clearInterval(interval); };
  }, [dashboard]);

  useEffect(function() {
    if (!employee) return;
    if (employee.employee_type !== 'fleet_manager') return;
    if (!dashboard) return;
    if (!dashboard.is_checked_in) return;
    if (!navigator.geolocation) return;
    
    var lastUpdateTime = 0;
    var MIN_UPDATE_INTERVAL = 20000; // 20 seconds minimum between updates
    
    var watchId = navigator.geolocation.watchPosition(
      function(pos) {
        var now = Date.now();
        // Throttle updates to prevent too frequent API calls
        if (now - lastUpdateTime < MIN_UPDATE_INTERVAL) {
          return;
        }
        lastUpdateTime = now;
        
        var loc = { 
          latitude: pos.coords.latitude, 
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy  // Include GPS accuracy
        };
        setCurrentLocation(loc);
        
        // Only send to server if accuracy is reasonable (< 100 meters)
        if (pos.coords.accuracy && pos.coords.accuracy < 100) {
          fetch(API_URL + '/api/employee/location/' + employee.id, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(loc)
          }).then(function(r) { return r.json(); }).then(function(result) {
            if (result.distance_added_km > 0) {
              console.log('Distance added:', result.distance_added_km, 'km');
            }
          });
        } else {
          console.log('GPS accuracy too low:', pos.coords.accuracy, 'm - skipping update');
        }
      },
      function(err) { console.error('Geolocation error:', err); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return function() { navigator.geolocation.clearWatch(watchId); };
  }, [employee, dashboard]);

  function loadData() {
    fetch(API_URL + '/api/employee/dashboard/' + employee.id)
      .then(function(res) { return res.json(); })
      .then(function(data) { setDashboard(data); setLoading(false); });
    fetch(API_URL + '/api/employee/tasks/' + employee.id)
      .then(function(res) { return res.json(); })
      .then(function(data) { setTasks(data); });
    if (employee.employee_type === 'flex_employee') {
      loadWeeklySchedule(null);
      // Use predefined slots endpoint (24+ hours away)
      fetch(API_URL + '/api/employee/slots/predefined-available')
        .then(function(res) { return res.json(); })
        .then(function(data) { setAvailableSlots(data); });
    }
  }

  function loadWeeklySchedule(startDate) {
    var url = API_URL + '/api/employee/slots/weekly/' + employee.id;
    if (startDate) url += '?start_date=' + startDate;
    fetch(url)
      .then(function(res) { return res.json(); })
      .then(function(data) { setWeeklySchedule(data.schedule); setWeekStart(data.week_start); });
  }

  function navigateWeek(direction) {
    var current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    loadWeeklySchedule(current.toISOString().split('T')[0]);
  }

  function handleBookSlot(slotId) {
    fetch(API_URL + '/api/employee/slots/book', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slot_id: slotId, employee_id: employee.id })
    })
      .then(function(res) { if (!res.ok) return res.json().then(function(d) { throw new Error(d.detail); }); return res.json(); })
      .then(function() { toast.success('Slot booked!'); loadData(); })
      .catch(function(e) { toast.error(e.message); });
  }

  function handleCancelSlot(slotId) {
    fetch(API_URL + '/api/employee/slots/cancel', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slot_id: slotId, employee_id: employee.id })
    })
      .then(function() { toast.success('Cancelled'); loadData(); });
  }

  function handleCheckIn() {
    setCheckingIn(true);
    fetch(API_URL + '/api/employee/check-in/' + employee.id, { method: 'POST' })
      .then(function(res) { if (!res.ok) return res.json().then(function(d) { throw new Error(d.detail); }); return res.json(); })
      .then(function() { toast.success('Checked in!'); loadData(); })
      .catch(function(e) { toast.error(e.message); })
      .finally(function() { setCheckingIn(false); });
  }

  function handleCheckOut() {
    if (!window.confirm('Check out?')) return;
    setCheckingIn(true);
    fetch(API_URL + '/api/employee/check-out/' + employee.id, { method: 'POST' })
      .then(function(res) { if (!res.ok) return res.json().then(function(d) { throw new Error(d.detail); }); return res.json(); })
      .then(function() { toast.success('Checked out!'); loadData(); })
      .catch(function(e) { toast.error(e.message); })
      .finally(function() { setCheckingIn(false); });
  }

  function handleTaskStatus(taskId, status, comment) {
    var url = API_URL + '/api/employee/tasks/' + taskId + '/status?status=' + status + '&employee_id=' + employee.id;
    if (comment) {
      url += '&comment=' + encodeURIComponent(comment);
    }
    fetch(url, { method: 'PUT' })
      .then(function(res) { return res.json(); })
      .then(function(data) { 
        if (data.bonus_achieved) {
          toast.success(data.bonus_achieved.message);
        } else {
          toast.success('Task status updated to ' + status); 
        }
        loadData(); 
      })
      .catch(function() { toast.error('Failed to update'); });
  }

  function handleLogout() {
    localStorage.removeItem('employee_token');
    localStorage.removeItem('employee_data');
    navigate('/employee-portal');
  }

  function handleChangePassword() {
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    fetch(API_URL + '/api/employee/change-password/' + employee.id, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        current_password: passwordForm.current,
        new_password: passwordForm.new_password
      })
    })
      .then(function(r) { 
        if (!r.ok) return r.json().then(function(d) { throw new Error(d.detail); }); 
        return r.json(); 
      })
      .then(function() {
        toast.success('Password changed successfully!');
        setShowChangePassword(false);
        setPasswordForm({ current: '', new_password: '', confirm: '' });
      })
      .catch(function(e) { toast.error(e.message); })
      .finally(function() { setChangingPassword(false); });
  }

  if (loading || !employee) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  var isFleet = employee.employee_type === 'fleet_manager';
  var isFlex = employee.employee_type === 'flex_employee';
  var typeGradient = isFleet ? 'from-blue-600 to-cyan-600' : isFlex ? 'from-amber-600 to-orange-600' : 'from-emerald-600 to-teal-600';
  var typeLabel = isFleet ? 'Fleet Manager' : isFlex ? 'Flex Employee' : 'HR/Operational';
  
  var dutyProgress = 0;
  var hoursWorked = 0;
  var remainingHours = 9;
  var isCheckedIn = false;
  var tasksPending = 0;
  var tasksInProgress = 0;
  var tasksCompleted = 0;
  var totalHours = 0;
  var attendanceHistory = [];
  var streak = { current: 0, longest: 0, message: 'Start your streak today!' };

  if (liveHours) {
    dutyProgress = parseFloat(liveHours.progress);
    hoursWorked = liveHours.worked;
    remainingHours = liveHours.remaining;
  } else if (dashboard && dashboard.current_shift) {
    dutyProgress = dashboard.current_shift.progress_percentage || 0;
    hoursWorked = dashboard.current_shift.current_hours_worked || 0;
    remainingHours = dashboard.current_shift.remaining_hours || 9;
  }

  if (dashboard) {
    isCheckedIn = dashboard.is_checked_in || false;
    if (dashboard.tasks) {
      tasksPending = dashboard.tasks.pending || 0;
      tasksInProgress = dashboard.tasks.in_progress || 0;
      tasksCompleted = dashboard.tasks.completed || 0;
    }
    if (dashboard.employee && dashboard.employee.total_hours_worked) {
      totalHours = dashboard.employee.total_hours_worked.toFixed(1);
    }
    if (dashboard.attendance_history) {
      attendanceHistory = dashboard.attendance_history;
    }
    if (dashboard.streak) {
      streak = dashboard.streak;
    }
  }

  var strokeDasharray = 2 * Math.PI * 88;
  var strokeDashoffset = strokeDasharray * (1 - dutyProgress / 100);
  var strokeColor = !isCheckedIn ? '#64748b' : (dutyProgress >= 100 ? '#22c55e' : '#8b5cf6');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="employee-portal-dashboard">
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={'w-10 h-10 bg-gradient-to-br ' + typeGradient + ' rounded-xl flex items-center justify-center text-white'}>
              {isFleet && <Truck className="w-5 h-5" />}
              {isFlex && <Clock className="w-5 h-5" />}
              {!isFleet && !isFlex && <Briefcase className="w-5 h-5" />}
            </div>
            <div><h1 className="text-lg font-bold text-white">{employee.name}</h1><p className="text-xs text-gray-400">{typeLabel}</p></div>
          </div>
          <div className="flex items-center gap-3">
            {isFleet && <NotificationBell employeeId={employee.id} />}
            <Button variant="ghost" size="sm" onClick={function() { setShowChangePassword(true); }} className="text-gray-400 hover:text-white" data-testid="change-password-btn">
              <Settings className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={loadData} className="text-gray-400"><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="ghost" onClick={handleLogout} className="text-gray-400 hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <DutyProgressCard isCheckedIn={isCheckedIn} hoursWorked={hoursWorked} remainingHours={remainingHours} strokeColor={strokeColor} strokeDasharray={strokeDasharray} strokeDashoffset={strokeDashoffset} checkingIn={checkingIn} typeGradient={typeGradient} onCheckIn={handleCheckIn} onCheckOut={handleCheckOut} />
          <OverviewCard tasksPending={tasksPending} tasksInProgress={tasksInProgress} tasksCompleted={tasksCompleted} totalHours={totalHours} isFleet={isFleet} isFlex={isFlex} isCheckedIn={isCheckedIn} currentLocation={currentLocation} streak={streak} todayKm={dashboard ? dashboard.today_km : 0} weeklyBadge={dashboard ? dashboard.weekly_badge : null} />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50 border border-white/10 mb-6 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600">Tasks</TabsTrigger>
            {isFlex && <TabsTrigger value="schedule" className="data-[state=active]:bg-purple-600">Weekly Schedule</TabsTrigger>}
            {isFlex && <TabsTrigger value="book" className="data-[state=active]:bg-purple-600">Book Slots</TabsTrigger>}
            {isFlex && <TabsTrigger value="earnings" className="data-[state=active]:bg-green-600">Earnings</TabsTrigger>}
            <TabsTrigger value="kyc" className="data-[state=active]:bg-purple-600"><User className="w-4 h-4 mr-1" />KYC/Profile</TabsTrigger>
            {isFleet && <TabsTrigger value="distance" className="data-[state=active]:bg-blue-600">Distance</TabsTrigger>}
            {isFleet && <TabsTrigger value="badges" className="data-[state=active]:bg-amber-600">Badges</TabsTrigger>}
            {isFleet && <TabsTrigger value="salary" className="data-[state=active]:bg-green-600"><FileText className="w-4 h-4 mr-1" />Salary</TabsTrigger>}
            {isFleet && <TabsTrigger value="location" className="data-[state=active]:bg-purple-600">Location</TabsTrigger>}
            <TabsTrigger value="holidays" className="data-[state=active]:bg-red-600"><Calendar className="w-4 h-4 mr-1" />Holidays</TabsTrigger>
            {!isFlex && <TabsTrigger value="duty" className="data-[state=active]:bg-green-600"><ClipboardList className="w-4 h-4 mr-1" />Duty</TabsTrigger>}
            <TabsTrigger value="files" className="data-[state=active]:bg-purple-600"><FileText className="w-4 h-4 mr-1" />Files</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-purple-600">Attendance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card className="bg-slate-800/50 border-white/10">
              <CardHeader><CardTitle className="text-white">My Tasks</CardTitle></CardHeader>
              <CardContent>
                <TaskList tasks={tasks} onStatusUpdate={handleTaskStatus} onRefresh={loadData} employeeId={employee.id} />
              </CardContent>
            </Card>
          </TabsContent>

          {isFlex && <TabsContent value="schedule"><WeeklyScheduleView schedule={weeklySchedule} weekStart={weekStart} onNavigate={navigateWeek} onCancel={handleCancelSlot} /></TabsContent>}
          {isFlex && <TabsContent value="book"><AvailableSlotsView slots={availableSlots} employeeId={employee.id} onBook={handleBookSlot} /></TabsContent>}
          {isFlex && <TabsContent value="earnings"><EarningsView employeeId={employee.id} /></TabsContent>}
          <TabsContent value="kyc"><KYCProfileView employeeId={employee.id} employeeType={employee.employee_type} /></TabsContent>
          {isFleet && <TabsContent value="distance"><FleetDistanceView employeeId={employee.id} /></TabsContent>}
          {isFleet && <TabsContent value="badges"><FleetBadgesView employeeId={employee.id} weeklyBadge={dashboard ? dashboard.weekly_badge : null} /></TabsContent>}
          {isFleet && <TabsContent value="salary"><SalarySlipsView employeeId={employee.id} /></TabsContent>}
          {isFleet && <TabsContent value="location"><LocationView currentLocation={currentLocation} isCheckedIn={isCheckedIn} /></TabsContent>}
          <TabsContent value="holidays"><HolidayCalendarView /></TabsContent>
          {!isFlex && <TabsContent value="duty"><DutySummaryView employeeId={employee.id} /></TabsContent>}
          <TabsContent value="files"><EmployeeFilesView employeeId={employee.id} /></TabsContent>
          <TabsContent value="history"><AttendanceHistoryView records={attendanceHistory} /></TabsContent>
        </Tabs>
      </main>

      {/* Change Password Modal */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-slate-800 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-emerald-400" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Current Password</label>
              <Input 
                type="password" 
                value={passwordForm.current} 
                onChange={function(e) { setPasswordForm(Object.assign({}, passwordForm, { current: e.target.value })); }} 
                placeholder="Enter current password" 
                className="bg-slate-700/50 border-white/10 text-white" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">New Password (min 6 chars)</label>
              <Input 
                type="password" 
                value={passwordForm.new_password} 
                onChange={function(e) { setPasswordForm(Object.assign({}, passwordForm, { new_password: e.target.value })); }} 
                placeholder="Enter new password" 
                className="bg-slate-700/50 border-white/10 text-white" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Confirm New Password</label>
              <Input 
                type="password" 
                value={passwordForm.confirm} 
                onChange={function(e) { setPasswordForm(Object.assign({}, passwordForm, { confirm: e.target.value })); }} 
                placeholder="Confirm new password" 
                className="bg-slate-700/50 border-white/10 text-white" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleChangePassword} disabled={changingPassword} className="bg-emerald-600 hover:bg-emerald-700">
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DutyProgressCard(props) {
  var isCheckedIn = props.isCheckedIn;
  var hoursWorked = props.hoursWorked;
  var remainingHours = props.remainingHours;
  var strokeColor = props.strokeColor;
  var strokeDasharray = props.strokeDasharray;
  var strokeDashoffset = props.strokeDashoffset;
  var checkingIn = props.checkingIn;
  var typeGradient = props.typeGradient;
  var onCheckIn = props.onCheckIn;
  var onCheckOut = props.onCheckOut;

  return (
    <Card className="bg-slate-800/50 border-white/10 lg:col-span-1">
      <CardContent className="p-6 flex flex-col items-center justify-center">
        <div className="relative w-48 h-48">
          <svg className="w-full h-full transform -rotate-90">
            <circle cx="96" cy="96" r="88" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12" />
            <circle cx="96" cy="96" r="88" fill="none" stroke={strokeColor} strokeWidth="12" strokeLinecap="round" strokeDasharray={strokeDasharray} strokeDashoffset={strokeDashoffset} className="transition-all duration-1000" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {isCheckedIn ? (
              <div className="text-center">
                <span className="text-3xl font-bold text-white">{hoursWorked}h</span>
                <p className="text-sm text-gray-400">of 9h completed</p>
                <p className="text-xs text-purple-400 mt-1">{remainingHours}h remaining</p>
              </div>
            ) : (
              <div className="text-center">
                <Clock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <span className="text-sm text-gray-400">Not checked in</span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 w-full">
          {isCheckedIn ? (
            <Button onClick={onCheckOut} disabled={checkingIn} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="checkout-btn">
              <Pause className="w-4 h-4 mr-2" />{checkingIn ? 'Processing...' : 'Check Out'}
            </Button>
          ) : (
            <Button onClick={onCheckIn} disabled={checkingIn} className={'w-full bg-gradient-to-r ' + typeGradient + ' text-white'} data-testid="checkin-btn">
              <Play className="w-4 h-4 mr-2" />{checkingIn ? 'Processing...' : 'Check In'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function OverviewCard(props) {
  var tasksPending = props.tasksPending;
  var tasksInProgress = props.tasksInProgress;
  var tasksCompleted = props.tasksCompleted;
  var totalHours = props.totalHours;
  var isFleet = props.isFleet;
  var isFlex = props.isFlex;
  var isCheckedIn = props.isCheckedIn;
  var currentLocation = props.currentLocation;
  var streak = props.streak || { current: 0, longest: 0, message: '' };
  var todayKm = props.todayKm || 0;
  var weeklyBadge = props.weeklyBadge;

  var streakColor = streak.current >= 10 ? 'text-orange-400' : streak.current >= 5 ? 'text-amber-400' : 'text-gray-400';
  var streakBgColor = streak.current >= 10 ? 'bg-orange-500/10 border-orange-500/30' : streak.current >= 5 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-700/30';

  // Badge display for Fleet Manager
  var badgeDisplay = null;
  if (isFleet && weeklyBadge) {
    var badgeEmoji = weeklyBadge.badge_type === 'gold' ? '🥇' : weeklyBadge.badge_type === 'silver' ? '🥈' : '🥉';
    var badgeClass = weeklyBadge.badge_type === 'gold' ? 'bg-yellow-500/20 text-yellow-300' : weeklyBadge.badge_type === 'silver' ? 'bg-slate-400/20 text-slate-300' : 'bg-amber-700/20 text-amber-400';
    badgeDisplay = (
      <Badge className={badgeClass + ' ml-2'}>
        {badgeEmoji} {weeklyBadge.badge_type}
      </Badge>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10 lg:col-span-2">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          Today's Overview
          {badgeDisplay}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-slate-700/30 rounded-lg text-center"><ClipboardList className="w-6 h-6 text-amber-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{tasksPending}</p><p className="text-xs text-gray-400">Pending</p></div>
          <div className="p-4 bg-slate-700/30 rounded-lg text-center"><Timer className="w-6 h-6 text-blue-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{tasksInProgress}</p><p className="text-xs text-gray-400">In Progress</p></div>
          <div className="p-4 bg-slate-700/30 rounded-lg text-center"><CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{tasksCompleted}</p><p className="text-xs text-gray-400">Completed</p></div>
          <div className="p-4 bg-slate-700/30 rounded-lg text-center"><History className="w-6 h-6 text-purple-400 mx-auto mb-2" /><p className="text-2xl font-bold text-white">{totalHours}h</p><p className="text-xs text-gray-400">Total Hours</p></div>
        </div>
        
        {/* Fleet Manager: Today's KM */}
        {isFleet && (
          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="fleet-today-km">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500">
                  <Navigation className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">Today's Distance</p>
                  <p className="text-sm text-gray-400">{isCheckedIn ? 'GPS tracking active' : 'Check in to start tracking'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-400">{todayKm.toFixed(2)}</p>
                <p className="text-xs text-gray-400">km driven today</p>
              </div>
            </div>
            {isCheckedIn && currentLocation && (
              <div className="mt-3 pt-3 border-t border-blue-500/30 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Location: {currentLocation.latitude.toFixed(5)}, {currentLocation.longitude.toFixed(5)}</p>
                  {currentLocation.accuracy && (
                    <p className={"text-xs " + (currentLocation.accuracy < 50 ? "text-green-400" : currentLocation.accuracy < 100 ? "text-yellow-400" : "text-red-400")}>
                      GPS Accuracy: {currentLocation.accuracy.toFixed(0)}m {currentLocation.accuracy < 50 ? '✓ Good' : currentLocation.accuracy < 100 ? '⚠ Fair' : '✗ Poor'}
                    </p>
                  )}
                </div>
                <a href={'https://www.google.com/maps?q=' + currentLocation.latitude + ',' + currentLocation.longitude} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">View on Map →</a>
              </div>
            )}
            {!isCheckedIn && (
              <div className="mt-3 pt-3 border-t border-blue-500/30">
                <p className="text-xs text-amber-400">⚠ You must check in to start GPS tracking</p>
              </div>
            )}
          </div>
        )}
        
        {/* Flex Employee: Attendance Streak */}
        {isFlex && (
          <div className={'mt-4 p-4 border rounded-lg ' + streakBgColor} data-testid="attendance-streak">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={'p-2 rounded-full ' + (streak.current >= 5 ? 'bg-gradient-to-br from-orange-500 to-amber-500' : 'bg-slate-600')}>
                  <Flame className={'w-6 h-6 ' + (streak.current >= 5 ? 'text-white' : 'text-gray-400')} />
                </div>
                <div>
                  <p className="text-white font-medium flex items-center gap-2">
                    Attendance Streak
                    {streak.current >= 10 && <Badge className="bg-orange-500/20 text-orange-300 text-xs">Hot!</Badge>}
                    {streak.current >= 20 && <Badge className="bg-red-500/20 text-red-300 text-xs">On Fire!</Badge>}
                  </p>
                  <p className="text-sm text-gray-400">{streak.message}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={'text-3xl font-bold ' + streakColor}>{streak.current}</p>
                <p className="text-xs text-gray-400">days</p>
              </div>
            </div>
            {streak.longest > 0 && streak.longest > streak.current && (
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-gray-400">Best streak: <span className="text-amber-400 font-medium">{streak.longest} days</span></span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
